import { useRef, useEffect } from "react"

/**
 * Toggle a value between included and excluded arrays.
 * If the value is in included, moves it to excluded. If in excluded, moves it to included.
 */
export function toggleIncludeExclude<T>(
  value: T,
  includedValues: T[],
  excludedValues: T[],
  onChangeIncluded: (values: T[]) => void,
  onChangeExcluded: (values: T[]) => void,
): void {
  if (includedValues.includes(value)) {
    // Move from included to excluded
    onChangeIncluded(includedValues.filter((v) => v !== value))
    onChangeExcluded([...excludedValues, value])
  } else {
    // Move from excluded to included
    onChangeExcluded(excludedValues.filter((v) => v !== value))
    onChangeIncluded([...includedValues, value])
  }
}

/**
 * Remove a value from both included and excluded arrays.
 */
export function removeFromBoth<T>(
  value: T,
  includedValues: T[],
  excludedValues: T[],
  onChangeIncluded: (values: T[]) => void,
  onChangeExcluded: (values: T[]) => void,
): void {
  onChangeIncluded(includedValues.filter((v) => v !== value))
  onChangeExcluded(excludedValues.filter((v) => v !== value))
}

/**
 * Hook to maintain stable display order for include/exclude filter items.
 * New items are added at the end, removed items are filtered out.
 * This prevents items from jumping around when toggling between include/exclude.
 *
 * @param includedValues - Array of included values
 * @param excludedValues - Array of excluded values
 * @returns Ordered array of all selected values
 */
export function useDisplayOrder(includedValues: string[], excludedValues: string[]): string[] {
  const displayOrderRef = useRef<string[]>([])
  const allSelectedSet = new Set([...includedValues, ...excludedValues])

  useEffect(() => {
    const currentOrder = displayOrderRef.current
    // Keep items that are still selected
    const keptItems = currentOrder.filter((item) => allSelectedSet.has(item))
    // Add new items that aren't in the current order
    const newItems = [...allSelectedSet].filter((item) => !currentOrder.includes(item))
    displayOrderRef.current = [...keptItems, ...newItems]
  }, [includedValues, excludedValues])

  // Return ordered items, including any not yet in displayOrder (for initial render)
  const orderedItems = displayOrderRef.current.filter((item) => allSelectedSet.has(item))
  const missingItems = [...allSelectedSet].filter((item) => !orderedItems.includes(item))

  return [...orderedItems, ...missingItems]
}
